import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Building2,
  Calendar,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  ExternalLink,
  Flame,
  Headphones,
  History,
  Keyboard,
  Mail,
  MessageSquare,
  Pause,
  Phone,
  PhoneCall,
  PhoneForwarded,
  PhoneOff,
  Play,
  RotateCcw,
  Search,
  Send,
  ShieldAlert,
  SkipForward,
  Sparkles,
  Square,
  Timer,
  User,
  Volume2,
  VolumeX,
  X,
  Zap,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { useToast } from "../../components/ui/toast";
import { Dropdown } from "../../components/ui/dropdown";
import { Modal } from "../../components/ui/modal";
import { DateTimePicker } from "../../components/ui/date-picker";
import { crmApi } from "./api";
import { LeadCallButton } from "./lead-call-button";
import { initialsOf, type CrmInteraction, type Lead } from "./types";

interface SessionMetrics {
  attempted: number;
  interested: number;
  callbacks: number;
  noAnswer: number;
  voicemail: number;
  notInterested: number;
  skipped: number;
}

function playChime(type: "start" | "next" | "complete" | "disposition", enabled = true) {
  if (!enabled) return;
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    if (type === "start") {
      osc.frequency.setValueAtTime(440, now);
      osc.frequency.exponentialRampToValueAtTime(880, now + 0.15);
      gain.gain.setValueAtTime(0.06, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
      osc.start(now);
      osc.stop(now + 0.2);
    } else if (type === "disposition") {
      osc.frequency.setValueAtTime(523.25, now);
      osc.frequency.setValueAtTime(659.25, now + 0.08);
      gain.gain.setValueAtTime(0.05, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.18);
      osc.start(now);
      osc.stop(now + 0.18);
    } else if (type === "next") {
      osc.frequency.setValueAtTime(659.25, now);
      gain.gain.setValueAtTime(0.04, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
      osc.start(now);
      osc.stop(now + 0.1);
    } else if (type === "complete") {
      osc.frequency.setValueAtTime(523.25, now);
      osc.frequency.setValueAtTime(659.25, now + 0.1);
      osc.frequency.setValueAtTime(783.99, now + 0.2);
      gain.gain.setValueAtTime(0.07, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc.start(now);
      osc.stop(now + 0.35);
    }
  } catch {
    // Ignore audio exceptions
  }
}

function formatDuration(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
}

function formatRelativeTime(isoStr?: string) {
  if (!isoStr) return "";
  try {
    const diff = Date.now() - new Date(isoStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return "";
  }
}

export function DialerPage() {
  const toast = useToast();
  const cache = useQueryClient();
  const campaigns = useQuery({ queryKey: ["crm-campaigns"], queryFn: crmApi.campaigns });
  const allLeads = useQuery({ queryKey: ["crm-leads"], queryFn: crmApi.leads });
  
  const [campaignId, setCampaignId] = useState("");
  const [attemptedIds, setAttemptedIds] = useState<string[]>([]);
  const [dialerMode, setDialerMode] = useState<"preview" | "power">("preview");
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [sessionSeconds, setSessionSeconds] = useState(0);
  const [copiedPhone, setCopiedPhone] = useState(false);
  
  // Quick Notes inside active HUD
  const [quickNote, setQuickNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  // Quick Follow-up modal/popover state
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [followUpDueAt, setFollowUpDueAt] = useState("");
  const [followUpNote, setFollowUpNote] = useState("");

  // DND confirmation modal
  const [dndModalOpen, setDndModalOpen] = useState(false);
  const [dndReason, setDndReason] = useState("Customer opt-out");

  // Auto-advance wrap-up timer (Power mode)
  const [wrapUpSeconds, setWrapUpSeconds] = useState<number | null>(null);
  const wrapUpTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Queue search & tab filter
  const [queueSearch, setQueueSearch] = useState("");
  const [queueTab, setQueueTab] = useState<"all" | "queued" | "completed">("all");

  // Session Statistics
  const [metrics, setMetrics] = useState<SessionMetrics>({
    attempted: 0,
    interested: 0,
    callbacks: 0,
    noAnswer: 0,
    voicemail: 0,
    notInterested: 0,
    skipped: 0,
  });

  // Queries
  const queue = useQuery({
    queryKey: ["crm-dialer-queue", campaignId],
    queryFn: () => crmApi.dialerQueue(campaignId),
    enabled: !!campaignId,
    refetchInterval: 20000,
  });

  const state = useQuery({
    queryKey: ["crm-dialer-state", campaignId],
    queryFn: () => crmApi.dialerState(campaignId),
    enabled: !!campaignId,
  });

  const refresh = useCallback(() => Promise.all([
    cache.invalidateQueries({ queryKey: ["crm-dialer-queue", campaignId] }),
    cache.invalidateQueries({ queryKey: ["crm-dialer-state", campaignId] }),
    cache.invalidateQueries({ queryKey: ["crm-leads"] }),
  ]), [cache, campaignId]);

  const items = useMemo(() => queue.data?.items ?? [], [queue.data?.items]);
  const remaining = useMemo(() => items.filter((item) => !attemptedIds.includes(item.id)), [items, attemptedIds]);
  const current = remaining[0] ?? null;
  const position = current ? items.findIndex((item) => item.id === current.id) + 1 : 0;
  const running = state.data?.status === "active";

  // Find full lead record for rich context
  const fullLead = useMemo<Lead | null>(() => {
    if (!current) return null;
    return allLeads.data?.find((l) => l.id === current.id) ?? null;
  }, [allLeads.data, current]);

  // Lead interactions history
  const leadHistory = useQuery({
    queryKey: ["crm-history", current?.id],
    queryFn: () => (current?.id ? crmApi.history(current.id) : Promise.resolve([])),
    enabled: !!current?.id,
  });

  // Session active timer ticking
  useEffect(() => {
    if (!running) return;
    const interval = setInterval(() => {
      setSessionSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [running]);

  // Reset quick note when current lead changes
  useEffect(() => {
    setQuickNote("");
  }, [current?.id]);

  // Handle Wrap-up countdown timer for power mode
  useEffect(() => {
    if (wrapUpSeconds === null) return;
    if (wrapUpSeconds <= 0) {
      setWrapUpSeconds(null);
      // Advance to next lead in queue
      if (current) {
        setAttemptedIds((prev) => (prev.includes(current.id) ? prev : [...prev, current.id]));
        playChime("next", soundEnabled);
        void refresh();
      }
      return;
    }

    wrapUpTimerRef.current = setTimeout(() => {
      setWrapUpSeconds((prev) => (prev !== null ? prev - 1 : null));
    }, 1000);

    return () => {
      if (wrapUpTimerRef.current) clearTimeout(wrapUpTimerRef.current);
    };
  }, [wrapUpSeconds, current, soundEnabled, refresh]);

  const cancelWrapUp = () => {
    if (wrapUpTimerRef.current) clearTimeout(wrapUpTimerRef.current);
    setWrapUpSeconds(null);
  };

  const advance = useCallback((id: string, outcomeType?: keyof SessionMetrics) => {
    cancelWrapUp();
    setAttemptedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    if (outcomeType) {
      setMetrics((m) => ({ ...m, [outcomeType]: m[outcomeType] + 1, attempted: m.attempted + 1 }));
    }
    void refresh();

    // Check if session will be complete after this
    if (remaining.length <= 1) {
      playChime("complete", soundEnabled);
      toast.success("Dialer Queue Completed!", "All callable leads in this session have been attempted.");
    } else {
      playChime("next", soundEnabled);
    }
  }, [remaining.length, soundEnabled, toast, refresh]);

  const setStatus = async (status: "active" | "paused" | "stopped") => {
    if (!campaignId) return;
    cancelWrapUp();
    await crmApi.setDialerState(campaignId, status);
    if (status === "stopped") {
      setAttemptedIds([]);
      setSessionSeconds(0);
      setMetrics({
        attempted: 0,
        interested: 0,
        callbacks: 0,
        noAnswer: 0,
        voicemail: 0,
        notInterested: 0,
        skipped: 0,
      });
    } else if (status === "active") {
      playChime("start", soundEnabled);
    }
    await refresh();
  };

  // 1-Click Dispositions
  const handleQuickDisposition = async (
    type: "interested" | "noAnswer" | "voicemail" | "notInterested",
    outcomeLabel: string,
    leadStatus: string,
    defaultNote: string,
  ) => {
    if (!current) return;
    const finalNote = quickNote.trim() || defaultNote;
    try {
      await crmApi.log(current.id, {
        id: crypto.randomUUID(),
        kind: "call",
        outcome: outcomeLabel,
        status: leadStatus,
        notes: finalNote,
      });
      await crmApi.updateLead(current.id, { status: leadStatus });
      playChime("disposition", soundEnabled);
      toast.success(`Logged as ${outcomeLabel}`, `${current.name} updated to ${leadStatus}`);

      if (dialerMode === "power" && remaining.length > 1) {
        setMetrics((m) => ({ ...m, [type]: m[type] + 1, attempted: m.attempted + 1 }));
        setAttemptedIds((prev) => (prev.includes(current.id) ? prev : [...prev, current.id]));
        setWrapUpSeconds(5);
        void refresh();
      } else {
        advance(current.id, type);
      }
    } catch {
      toast.danger("Failed to log outcome", "Please check your network and try again.");
    }
  };

  // Save quick note without advancing
  const saveQuickNoteOnly = async () => {
    if (!current || !quickNote.trim()) return;
    setSavingNote(true);
    try {
      await crmApi.log(current.id, {
        id: crypto.randomUUID(),
        kind: "note",
        notes: quickNote.trim(),
      });
      toast.success("Note saved", "Added to lead's activity history.");
      setQuickNote("");
      void cache.invalidateQueries({ queryKey: ["crm-history", current.id] });
    } catch {
      toast.danger("Could not save note");
    } finally {
      setSavingNote(false);
    }
  };

  // Follow-up submission
  const submitFollowUp = async () => {
    if (!current) return;
    if (!followUpDueAt) {
      toast.danger("Date required", "Please choose a date and time for the follow-up.");
      return;
    }
    try {
      await crmApi.log(current.id, {
        id: crypto.randomUUID(),
        kind: "call",
        outcome: "Follow-up",
        status: "Follow-up",
        notes: quickNote.trim() || followUpNote || "Callback requested",
        followUp: {
          dueAt: new Date(followUpDueAt).toISOString(),
          note: followUpNote || quickNote.trim() || "Follow-up call",
        },
      });
      await crmApi.updateLead(current.id, { status: "Follow-up" });
      playChime("disposition", soundEnabled);
      toast.success("Follow-up scheduled", `Callback set for ${new Date(followUpDueAt).toLocaleString()}`);
      setFollowUpModalOpen(false);
      setFollowUpDueAt("");
      setFollowUpNote("");
      advance(current.id, "callbacks");
    } catch {
      toast.danger("Could not save follow-up");
    }
  };

  // Suppression / DND submission
  const submitSuppression = async () => {
    if (!current) return;
    try {
      if (current.phone) {
        await crmApi.addSuppression({ phone: current.phone, reason: dndReason });
      }
      await crmApi.log(current.id, {
        id: crypto.randomUUID(),
        kind: "compliance",
        notes: `Suppressed from calling list: ${dndReason}`,
      });
      playChime("disposition", soundEnabled);
      toast.success("Added to DND list", `${current.phone} suppressed from future dialer queues.`);
      setDndModalOpen(false);
      advance(current.id, "skipped");
    } catch {
      toast.danger("Could not add to DND");
    }
  };

  // Copy phone helper
  const copyPhone = () => {
    if (!current?.phone) return;
    navigator.clipboard.writeText(current.phone);
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  // Keyboard Shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in input or textarea or modal is open
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        followUpModalOpen ||
        dndModalOpen ||
        showShortcuts
      ) {
        return;
      }

      if (e.key === " " && campaignId) {
        e.preventDefault();
        if (state.data?.status === "active") {
          void setStatus("paused");
        } else if (queue.data?.openNow && remaining.length > 0) {
          void setStatus("active");
        }
      } else if (e.key.toLowerCase() === "s" && current && running) {
        e.preventDefault();
        advance(current.id, "skipped");
      } else if (e.key === "1" && current && running) {
        e.preventDefault();
        void handleQuickDisposition("interested", "Connected", "Interested", "Customer interested");
      } else if (e.key === "2" && current && running) {
        e.preventDefault();
        setFollowUpModalOpen(true);
      } else if (e.key === "3" && current && running) {
        e.preventDefault();
        void handleQuickDisposition("noAnswer", "No answer", "Contacted", "No answer / ringing");
      } else if (e.key === "4" && current && running) {
        e.preventDefault();
        void handleQuickDisposition("voicemail", "Voicemail", "Contacted", "Left voicemail");
      } else if (e.key === "5" && current && running) {
        e.preventDefault();
        void handleQuickDisposition("notInterested", "Not interested", "Not interested", "Not interested");
      } else if (e.key === "?") {
        e.preventDefault();
        setShowShortcuts((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaignId,
    state.data?.status,
    queue.data?.openNow,
    remaining.length,
    current,
    running,
    followUpModalOpen,
    dndModalOpen,
    showShortcuts,
    advance,
  ]);

  // Campaign dropdown options
  const campaignOptions = useMemo(
    () => (campaigns.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [campaigns.data],
  );

  // Filtered queue items for table
  const filteredQueueItems = useMemo(() => {
    return items.filter((item) => {
      const isDone = attemptedIds.includes(item.id);
      if (queueTab === "queued" && isDone) return false;
      if (queueTab === "completed" && !isDone) return false;
      if (!queueSearch.trim()) return true;
      const q = queueSearch.toLowerCase();
      return (
        item.name.toLowerCase().includes(q) ||
        item.phone.toLowerCase().includes(q) ||
        (item.company && item.company.toLowerCase().includes(q))
      );
    });
  }, [items, attemptedIds, queueTab, queueSearch]);

  const totalProgressPercent = items.length > 0 ? Math.round((attemptedIds.length / items.length) * 100) : 0;

  return (
    <div className="flex flex-col p-4 sm:p-5 md:p-6 space-y-4 w-full">
      {/* 1. Header & Configuration Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-ink dark:text-white">Dialer</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Custom Campaign Dropdown */}
          <div className="w-52 sm:w-60">
            <Dropdown
              ariaLabel="Select campaign"
              value={campaignId}
              options={[{ value: "", label: "Choose campaign…" }, ...campaignOptions]}
              onChange={(val) => {
                setCampaignId(val);
                setAttemptedIds([]);
                setWrapUpSeconds(null);
              }}
              className="bg-elevated dark:bg-stone-900 border-line dark:border-white/20 text-ink dark:text-stone-100 shadow-xs font-medium text-xs sm:text-sm py-1.5"
            />
          </div>

          {/* Schedule status pill */}
          {queue.data && (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border",
                queue.data.openNow
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700/60"
                  : "bg-soft dark:bg-stone-800 text-ink-3 dark:text-stone-300 border-line dark:border-white/10",
              )}
            >
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  queue.data.openNow ? "bg-emerald-500 animate-pulse" : "bg-ink-faint dark:bg-stone-500",
                )}
              />
              {queue.data.openNow ? "Schedule open" : "Closed"}
            </span>
          )}

          {/* Mode Switcher Toggle: Preview vs Power */}
          <div className="flex items-center rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 p-0.5 text-xs font-medium">
            <button
              onClick={() => setDialerMode("preview")}
              className={cn(
                "flex items-center gap-1 rounded-md px-2 py-0.5 transition-all",
                dialerMode === "preview"
                  ? "bg-elevated dark:bg-stone-700 text-ink dark:text-white shadow-xs font-semibold"
                  : "text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white",
              )}
              title="Preview mode: Agent reviews lead and triggers call manually"
            >
              Preview
            </button>
            <button
              onClick={() => setDialerMode("power")}
              className={cn(
                "flex items-center gap-1 rounded-md px-2 py-0.5 transition-all",
                dialerMode === "power"
                  ? "bg-brand text-white shadow-xs font-semibold"
                  : "text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white",
              )}
              title="Power mode: Auto-countdown wrap-up timer to advance through leads"
            >
              <Zap className="h-3 w-3" /> Power
            </button>
          </div>

          {/* Sound Toggle */}
          <button
            onClick={() => setSoundEnabled((v) => !v)}
            title={soundEnabled ? "Mute audio cues" : "Unmute audio cues"}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line dark:border-white/15 bg-elevated dark:bg-stone-800 text-ink-3 dark:text-stone-300 hover:bg-soft dark:hover:bg-stone-700 hover:text-ink dark:hover:text-white transition-colors"
          >
            {soundEnabled ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5 text-ink-faint dark:text-stone-500" />}
          </button>

          {/* Keyboard Shortcuts button */}
          <button
            onClick={() => setShowShortcuts(true)}
            title="Keyboard shortcuts (?)"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line dark:border-white/15 bg-elevated dark:bg-stone-800 text-ink-3 dark:text-stone-300 hover:bg-soft dark:hover:bg-stone-700 hover:text-ink dark:hover:text-white transition-colors"
          >
            <Keyboard className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {!campaignId ? (
        <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-8 text-center shadow-card dark:shadow-card-dark">
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand dark:bg-brand/20 mb-3">
            <Headphones className="h-5 w-5" />
          </div>
          <h2 className="text-sm font-bold text-ink dark:text-white">Ready to Start Calling?</h2>
          <p className="mt-1 text-xs text-ink-3 dark:text-stone-300 max-w-md mx-auto">
            Select an active campaign above to load its dialing queue, customer context, and 1-click dispositions.
          </p>
        </div>
      ) : (
        <>
          {/* 2. Session Scoreboard & Controls Strip */}
          <div className="rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-3.5 sm:p-4 shadow-card dark:shadow-card-dark">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
              {/* Play / Pause / Stop Buttons */}
              <div className="flex items-center gap-2">
                {state.data?.status !== "active" ? (
                  <button
                    disabled={!queue.data?.openNow || remaining.length === 0}
                    onClick={() => void setStatus("active")}
                    title={!queue.data?.openNow ? "Campaign schedule is closed" : undefined}
                    className="flex items-center gap-1.5 rounded-lg bg-brand px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-brand-hover active:scale-95 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Play className="h-3.5 w-3.5 fill-white" />
                    {state.data?.status === "paused" ? "Resume" : "Start Session"}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => void setStatus("paused")}
                      className="flex items-center gap-1.5 rounded-lg border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-3 py-1.5 text-xs font-semibold text-ink dark:text-stone-100 hover:bg-soft dark:hover:bg-stone-700 active:scale-95 transition-all"
                    >
                      <Pause className="h-3.5 w-3.5" /> Pause
                    </button>
                    <button
                      onClick={() => void setStatus("stopped")}
                      className="flex items-center gap-1.5 rounded-lg border border-danger/30 bg-danger-soft dark:bg-red-950/40 px-3 py-1.5 text-xs font-semibold text-danger dark:text-red-300 hover:bg-danger/20 active:scale-95 transition-all"
                    >
                      <Square className="h-3.5 w-3.5 fill-danger" /> Stop
                    </button>
                  </>
                )}

                {/* Session Timer & Status Indicator */}
                <div className="flex items-center gap-2 border-l border-line dark:border-white/15 pl-2.5 text-xs text-ink-2 dark:text-stone-300">
                  <div className="flex items-center gap-1 font-mono font-semibold bg-soft dark:bg-stone-800 px-2 py-1 rounded-md border border-line dark:border-white/15 text-xs text-ink dark:text-stone-100">
                    <Timer className="h-3 w-3 text-brand" />
                    {formatDuration(sessionSeconds)}
                  </div>
                  <span className="hidden sm:inline font-medium text-xs text-ink-3 dark:text-stone-300">
                    {state.data?.status === "active" ? (
                      <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping" /> Live Calling
                      </span>
                    ) : state.data?.status === "paused" ? (
                      "Paused"
                    ) : (
                      "Ready"
                    )}
                  </span>
                </div>
              </div>

              {/* Session KPI Badges */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <div className="flex items-center gap-1.5 rounded-lg bg-soft dark:bg-stone-800/90 border border-line dark:border-white/15 px-2.5 py-1.5 font-medium text-ink-2 dark:text-stone-200">
                  <span className="text-ink-3 dark:text-stone-400">Queue:</span>
                  <span className="font-bold text-ink dark:text-white">{remaining.length} left</span>
                  <span className="text-ink-faint dark:text-stone-400">/ {items.length}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-soft dark:bg-stone-800/90 border border-line dark:border-white/15 px-2.5 py-1.5 font-medium text-ink-2 dark:text-stone-200">
                  <span className="text-ink-3 dark:text-stone-400">Attempted:</span>
                  <span className="font-bold text-ink dark:text-white">{metrics.attempted}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/20 dark:border-emerald-700/60 px-2.5 py-1.5 font-medium text-emerald-700 dark:text-emerald-300">
                  <Flame className="h-3.5 w-3.5" />
                  <span>Interested:</span>
                  <span className="font-bold">{metrics.interested}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-sky-500/10 dark:bg-sky-950/40 border border-sky-500/20 dark:border-sky-700/60 px-2.5 py-1.5 font-medium text-sky-700 dark:text-sky-300">
                  <CalendarClock className="h-3.5 w-3.5" />
                  <span>Callback:</span>
                  <span className="font-bold">{metrics.callbacks}</span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg bg-soft dark:bg-stone-800/90 border border-line dark:border-white/15 px-2.5 py-1.5 font-medium text-ink-3 dark:text-stone-300">
                  <span>No Ans/VM:</span>
                  <span className="font-semibold text-ink-2 dark:text-stone-100">{metrics.noAnswer + metrics.voicemail}</span>
                </div>
              </div>
            </div>

            {/* Overall Queue Progress Bar */}
            <div className="mt-3.5 pt-3 border-t border-line dark:border-white/15">
              <div className="flex items-center justify-between text-xs text-ink-3 dark:text-stone-300 mb-1.5">
                <span className="font-medium">Session Progress</span>
                <span className="font-semibold font-mono text-ink dark:text-stone-100">
                  {attemptedIds.length} of {items.length} leads ({totalProgressPercent}%)
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-soft dark:bg-stone-800 overflow-hidden border border-line/60 dark:border-white/15">
                <div
                  className="h-full bg-gradient-to-r from-brand to-amber-500 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${totalProgressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {/* 3. Active Call Heads-Up Display (HUD) */}
          {current && running ? (
            <div className="rounded-2xl border-2 border-brand/40 bg-elevated dark:bg-[#262320] p-5 sm:p-6 shadow-card-hover dark:shadow-card-dark transition-all">
              {/* Active Header & Position */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line dark:border-white/15 pb-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-2.5 w-2.5 rounded-full bg-brand animate-ping" />
                  <span className="text-xs font-bold uppercase tracking-wider text-brand">
                    Active Lead · #{position} of {items.length} in Queue
                  </span>
                  {fullLead?.source && (
                    <span className="rounded-md bg-soft dark:bg-stone-800 px-2 py-0.5 text-[11px] font-medium text-ink-3 dark:text-stone-300 border border-line dark:border-white/15">
                      Source: {fullLead.source}
                    </span>
                  )}
                  {fullLead?.status && (
                    <span className="rounded-md bg-brand-soft dark:bg-brand/20 px-2 py-0.5 text-[11px] font-semibold text-brand">
                      {fullLead.status}
                    </span>
                  )}
                </div>

                {/* Auto-Advance wrap-up countdown indicator */}
                {wrapUpSeconds !== null ? (
                  <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 dark:bg-amber-950/40 border border-amber-500/30 dark:border-amber-700/60 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-300">
                    <Timer className="h-4 w-4 animate-spin text-amber-500" />
                    <span>Next lead in <strong className="font-mono text-sm">{wrapUpSeconds}s</strong></span>
                    <button
                      onClick={cancelWrapUp}
                      className="ml-2 font-semibold underline hover:text-amber-800 dark:hover:text-amber-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => advance(current.id)}
                      className="rounded bg-amber-500 hover:bg-amber-600 px-2 py-0.5 text-white font-bold text-[11px]"
                    >
                      Dial Next Now
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-ink-3 dark:text-stone-400">
                    <Clock className="h-3.5 w-3.5" />
                    <span>Added {formatRelativeTime(current.createdAt)}</span>
                  </div>
                )}
              </div>

              {/* Main Lead Details Grid */}
              <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Column 1: Identity & Contact Card */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-brand to-amber-500 text-white font-extrabold text-xl shadow-md">
                      {initialsOf(current.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-lg font-bold text-ink dark:text-white truncate flex items-center gap-2">
                        {current.name || "Unnamed Contact"}
                        <Link
                          to={`/crm/leads/${current.id}`}
                          target="_blank"
                          title="Open full record in new tab"
                          className="text-ink-3 dark:text-stone-400 hover:text-brand dark:hover:text-brand"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                      </h2>
                      {current.company && (
                        <p className="text-sm text-ink-2 dark:text-stone-300 flex items-center gap-1.5 mt-0.5 truncate">
                          <Building2 className="h-3.5 w-3.5 text-ink-3 dark:text-stone-400 shrink-0" />
                          {current.company}
                        </p>
                      )}
                      {fullLead?.email && (
                        <p className="text-xs text-ink-3 dark:text-stone-400 flex items-center gap-1.5 mt-1 truncate">
                          <Mail className="h-3.5 w-3.5 text-ink-3 dark:text-stone-400 shrink-0" />
                          {fullLead.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Phone Pill with 1-Click Copy */}
                  <div className="flex items-center justify-between rounded-xl bg-soft dark:bg-stone-800/80 border border-line dark:border-white/15 p-3">
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-brand" />
                      <span className="font-mono text-sm font-bold text-ink dark:text-white tracking-wide">
                        {current.phone || "No phone number"}
                      </span>
                    </div>
                    <button
                      onClick={copyPhone}
                      title="Copy phone"
                      className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink-3 dark:text-stone-300 hover:bg-line dark:hover:bg-stone-700 hover:text-ink dark:hover:text-white transition-colors"
                    >
                      {copiedPhone ? (
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
                          <Check className="h-3 w-3" /> Copied
                        </span>
                      ) : (
                        <>
                          <Copy className="h-3 w-3" /> Copy
                        </>
                      )}
                    </button>
                  </div>

                  {/* Custom Fields snippet if any */}
                  {fullLead?.values && Object.keys(fullLead.values).length > 0 && (
                    <div className="rounded-xl border border-line dark:border-white/15 bg-white dark:bg-stone-800/40 p-3 space-y-1.5 text-xs">
                      <p className="font-semibold text-ink-3 dark:text-stone-400 uppercase text-[10px] tracking-wider">Custom Fields</p>
                      {Object.entries(fullLead.values).slice(0, 4).map(([key, val]) => (
                        <div key={key} className="flex justify-between items-center text-ink-2 dark:text-stone-300">
                          <span className="text-ink-3 dark:text-stone-400 capitalize">{key.replace(/_/g, " ")}:</span>
                          <span className="font-medium truncate max-w-[150px]">{String(val)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Column 2: In-Dialer Quick Notes & Context */}
                <div className="lg:col-span-1 flex flex-col justify-between space-y-3">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-stone-300 mb-1.5">
                      Call Notes & Action Items
                    </label>
                    <textarea
                      value={quickNote}
                      onChange={(e) => setQuickNote(e.target.value)}
                      placeholder="Jot talking points, requirements, or objections during the call... (auto-saved with disposition)"
                      rows={4}
                      className="w-full rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 p-3 text-sm text-ink dark:text-stone-100 placeholder:text-ink-faint dark:placeholder:text-stone-400 outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 transition-all resize-none"
                    />
                    <div className="mt-2 flex justify-between items-center text-xs">
                      <span className="text-ink-3 dark:text-stone-400 text-[11px]">Auto-saved when you click a disposition below</span>
                      <button
                        onClick={saveQuickNoteOnly}
                        disabled={savingNote || !quickNote.trim()}
                        className="flex items-center gap-1 rounded-lg border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-2.5 py-1 text-xs font-semibold text-ink dark:text-stone-100 hover:bg-soft dark:hover:bg-stone-700 disabled:opacity-40"
                      >
                        <Send className="h-3 w-3" /> Save note only
                      </button>
                    </div>
                  </div>

                  {/* Existing lead notes */}
                  {fullLead?.notes && (
                    <div className="rounded-xl bg-amber-500/5 dark:bg-amber-950/30 border border-amber-500/20 dark:border-amber-700/40 p-2.5 text-xs text-ink-2 dark:text-stone-300">
                      <span className="font-bold text-amber-700 dark:text-amber-400 block text-[10px] uppercase">
                        Lead Memo
                      </span>
                      <p className="line-clamp-2 mt-0.5">{fullLead.notes}</p>
                    </div>
                  )}
                </div>

                {/* Column 3: Recent Activity & Interaction History */}
                <div className="lg:col-span-1 rounded-xl border border-line dark:border-white/15 bg-white dark:bg-stone-800/40 p-3.5 flex flex-col justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-stone-300 mb-2 flex items-center gap-1.5">
                      <History className="h-3.5 w-3.5 text-brand" /> Past Touchpoints
                    </p>
                    {leadHistory.isLoading ? (
                      <p className="text-xs text-ink-3 dark:text-stone-400 py-4 text-center">Loading past interactions…</p>
                    ) : (leadHistory.data ?? []).length === 0 ? (
                      <div className="text-center py-6 text-xs text-ink-3 dark:text-stone-400">
                        <MessageSquare className="h-6 w-6 mx-auto text-ink-faint dark:text-stone-500 mb-1.5" />
                        <p>No past calls logged yet.</p>
                        <p className="text-[11px] text-ink-faint dark:text-stone-500">This will be the first recorded touchpoint.</p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                        {(leadHistory.data ?? []).slice(0, 3).map((item, idx) => (
                          <div key={item.id || idx} className="rounded-lg bg-soft dark:bg-stone-800 p-2 text-xs border border-line/60 dark:border-white/10">
                            <div className="flex items-center justify-between text-[11px] text-ink-3 dark:text-stone-400">
                              <span className="font-semibold text-ink-2 dark:text-stone-200 capitalize">{item.kind} · {item.outcome || "Logged"}</span>
                              <span>{formatRelativeTime(item.createdAt)}</span>
                            </div>
                            {item.notes && <p className="text-ink-2 dark:text-stone-300 text-xs mt-1 line-clamp-2">{item.notes}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-2 text-right">
                    <Link
                      to={`/crm/leads/${current.id}`}
                      target="_blank"
                      className="text-xs font-semibold text-brand hover:underline inline-flex items-center gap-1"
                    >
                      View all history <ChevronDown className="h-3 w-3 -rotate-90" />
                    </Link>
                  </div>
                </div>
              </div>

              {/* 4. Action Bar & 1-Click Dispositions */}
              <div className="mt-6 pt-5 border-t border-line dark:border-white/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Primary Calling & Skip Buttons */}
                <div className="flex items-center gap-2.5">
                  <LeadCallButton
                    lead={{
                      id: current.id,
                      campaignId,
                      name: current.name,
                      phone: current.phone,
                      company: current.company,
                      fresh: true,
                      createdAt: "",
                    }}
                    onLogged={() => advance(current.id, "interested")}
                    triggerClassName="flex items-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-3 text-sm font-bold shadow-md active:scale-95 transition-all"
                    label="Call Lead Now"
                  />
                  <button
                    onClick={() => advance(current.id, "skipped")}
                    title="Skip lead without dialing (S)"
                    className="flex items-center gap-1.5 rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-4 py-3 text-sm font-semibold text-ink-2 dark:text-stone-200 hover:bg-soft dark:hover:bg-stone-700 hover:text-ink dark:hover:text-white active:scale-95 transition-all"
                  >
                    <SkipForward className="h-4 w-4" /> Skip
                  </button>
                  <button
                    onClick={() => setDndModalOpen(true)}
                    title="Suppress number / DND"
                    className="flex items-center gap-1.5 rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-3 py-3 text-xs font-semibold text-ink-3 dark:text-stone-400 hover:text-danger dark:hover:text-red-400 hover:border-danger/30 hover:bg-danger-soft dark:hover:bg-red-950/30 transition-all"
                  >
                    <ShieldAlert className="h-3.5 w-3.5" /> DND
                  </button>
                </div>

                {/* 1-Click Fast Dispositions Bar */}
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-ink-3 dark:text-stone-300 mr-1">Log & Next:</span>
                  <button
                    onClick={() => handleQuickDisposition("interested", "Connected", "Interested", "Customer interested")}
                    className="flex items-center gap-1.5 rounded-lg bg-emerald-500/10 dark:bg-emerald-950/40 border border-emerald-500/30 dark:border-emerald-700/60 px-3 py-2 text-xs font-bold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20 active:scale-95 transition-all"
                    title="Shortcut key: 1"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Interested (1)
                  </button>
                  <button
                    onClick={() => setFollowUpModalOpen(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-sky-500/10 dark:bg-sky-950/40 border border-sky-500/30 dark:border-sky-700/60 px-3 py-2 text-xs font-bold text-sky-700 dark:text-sky-300 hover:bg-sky-500/20 active:scale-95 transition-all"
                    title="Shortcut key: 2"
                  >
                    <CalendarClock className="h-3.5 w-3.5" /> Callback (2)
                  </button>
                  <button
                    onClick={() => handleQuickDisposition("noAnswer", "No answer", "Contacted", "No answer / ringing")}
                    className="flex items-center gap-1.5 rounded-lg bg-amber-500/10 dark:bg-amber-950/40 border border-amber-500/30 dark:border-amber-700/60 px-3 py-2 text-xs font-bold text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 active:scale-95 transition-all"
                    title="Shortcut key: 3"
                  >
                    <PhoneOff className="h-3.5 w-3.5" /> No Ans (3)
                  </button>
                  <button
                    onClick={() => handleQuickDisposition("voicemail", "Voicemail", "Contacted", "Left voicemail message")}
                    className="flex items-center gap-1.5 rounded-lg bg-purple-500/10 dark:bg-purple-950/40 border border-purple-500/30 dark:border-purple-700/60 px-3 py-2 text-xs font-bold text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 active:scale-95 transition-all"
                    title="Shortcut key: 4"
                  >
                    <Volume2 className="h-3.5 w-3.5" /> Voicemail (4)
                  </button>
                  <button
                    onClick={() => handleQuickDisposition("notInterested", "Not interested", "Not interested", "Customer not interested")}
                    className="flex items-center gap-1.5 rounded-lg bg-soft dark:bg-stone-800 border border-line dark:border-white/15 px-3 py-2 text-xs font-bold text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white hover:bg-line/60 dark:hover:bg-stone-700 active:scale-95 transition-all"
                    title="Shortcut key: 5"
                  >
                    <X className="h-3.5 w-3.5" /> Not Int. (5)
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] p-8 text-center shadow-card dark:shadow-card-dark">
              {!queue.data?.openNow ? (
                <div className="max-w-md mx-auto">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600 mb-3">
                    <Clock className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-ink dark:text-white text-base">Campaign Schedule is Currently Closed</h3>
                  <p className="mt-1 text-xs text-ink-3 dark:text-stone-300">
                    The calling window opens during configured operating hours. You can adjust this in the Campaigns section.
                  </p>
                </div>
              ) : remaining.length === 0 ? (
                <div className="max-w-md mx-auto">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 mb-3">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-ink dark:text-white text-base">Queue Fully Cleared!</h3>
                  <p className="mt-1 text-xs text-ink-3 dark:text-stone-300">
                    Every eligible lead in this campaign was attempted this session. Great job!
                  </p>
                  <button
                    onClick={() => setAttemptedIds([])}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-4 py-2 text-xs font-semibold text-ink dark:text-stone-100 hover:bg-soft dark:hover:bg-stone-700"
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Restart Queue from Beginning
                  </button>
                </div>
              ) : (
                <div className="max-w-md mx-auto">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 dark:bg-brand/20 text-brand mb-3">
                    <Play className="h-6 w-6 fill-brand" />
                  </div>
                  <h3 className="font-bold text-ink dark:text-white text-base">Ready to Begin</h3>
                  <p className="mt-1.5 text-xs text-ink-3 dark:text-stone-300">
                    Press <strong className="text-brand font-semibold">Start Session</strong> above or hit <kbd className="px-1.5 py-0.5 rounded border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 font-mono text-[10px] text-ink dark:text-stone-100 shadow-xs">Space</kbd> to begin dialing down the queue.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 5. Enhanced Queue Table */}
          <div className="rounded-2xl border border-line dark:border-white/15 bg-elevated dark:bg-[#262320] shadow-card dark:shadow-card-dark overflow-hidden">
            {/* Table Controls Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-line dark:border-white/15 bg-stone-50 dark:bg-stone-800/40">
              {/* Filter Tabs */}
              <div className="flex items-center gap-1 rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 p-0.5 text-xs font-medium">
                <button
                  onClick={() => setQueueTab("all")}
                  className={cn(
                    "rounded-md px-3 py-1.5 transition-all",
                    queueTab === "all" ? "bg-elevated dark:bg-stone-700 font-bold text-ink dark:text-white shadow-xs" : "text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white",
                  )}
                >
                  All ({items.length})
                </button>
                <button
                  onClick={() => setQueueTab("queued")}
                  className={cn(
                    "rounded-md px-3 py-1.5 transition-all",
                    queueTab === "queued" ? "bg-elevated dark:bg-stone-700 font-bold text-ink dark:text-white shadow-xs" : "text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Queued ({remaining.length})
                </button>
                <button
                  onClick={() => setQueueTab("completed")}
                  className={cn(
                    "rounded-md px-3 py-1.5 transition-all",
                    queueTab === "completed" ? "bg-elevated dark:bg-stone-700 font-bold text-ink dark:text-white shadow-xs" : "text-ink-3 dark:text-stone-300 hover:text-ink dark:hover:text-white",
                  )}
                >
                  Done ({attemptedIds.length})
                </button>
              </div>

              {/* Table Search */}
              <div className="relative w-full sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
                <input
                  value={queueSearch}
                  onChange={(e) => setQueueSearch(e.target.value)}
                  placeholder="Search in queue…"
                  className="w-full rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 py-1.5 pl-8 pr-3 text-xs text-ink dark:text-stone-100 placeholder:text-ink-faint dark:placeholder:text-stone-400 outline-none focus:border-brand"
                />
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-stone-100 dark:bg-stone-800/80 text-xs font-semibold uppercase tracking-wider text-ink-2 dark:text-stone-300 border-b border-line dark:border-white/15">
                  <tr>
                    <th className="p-3.5 pl-4 w-12 text-center">#</th>
                    <th className="p-3.5">Lead / Contact</th>
                    <th className="p-3.5">Phone Number</th>
                    <th className="p-3.5">Pipeline Status</th>
                    <th className="p-3.5">Queue State</th>
                    <th className="p-3.5 pr-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line dark:divide-white/10">
                  {filteredQueueItems.map((item, i) => {
                    const done = attemptedIds.includes(item.id);
                    const isCurrent = current?.id === item.id;

                    return (
                      <tr
                        key={item.id}
                        className={cn(
                          "transition-colors group",
                          isCurrent && running
                            ? "bg-brand/5 dark:bg-brand/15"
                            : "hover:bg-stone-100 dark:hover:bg-stone-800/60",
                          done && "opacity-60 bg-stone-100/60 dark:bg-stone-900/40",
                        )}
                      >
                        <td className="p-3.5 pl-4 text-center font-mono text-xs text-ink-3 dark:text-stone-400">
                          {isCurrent && running ? (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white text-[10px] font-bold mx-auto">
                              ▶
                            </span>
                          ) : (
                            i + 1
                          )}
                        </td>
                        <td className="p-3.5 align-middle">
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-soft dark:bg-stone-800 border border-line/60 dark:border-white/15 text-xs font-bold text-ink-2 dark:text-stone-200">
                              {initialsOf(item.name)}
                            </span>
                            <div className="min-w-0">
                              <p className="font-semibold text-ink dark:text-stone-100 truncate text-sm">
                                {item.name || "Unnamed contact"}
                              </p>
                              {item.company && (
                                <p className="text-xs text-ink-3 dark:text-stone-400 truncate">{item.company}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="p-3.5 align-middle font-mono text-xs font-medium text-ink-2 dark:text-stone-300">
                          {item.phone}
                        </td>
                        <td className="p-3.5 align-middle text-xs">
                          <span className="rounded-md bg-soft dark:bg-stone-800/90 px-2 py-0.5 text-xs font-medium text-ink-2 dark:text-stone-300 border border-line dark:border-white/15">
                            {item.status || "Fresh"}
                          </span>
                        </td>
                        <td className="p-3.5 align-middle">
                          {done ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 dark:bg-emerald-950/40 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                              <Check className="h-3 w-3 text-emerald-600 dark:text-emerald-400" /> Done
                            </span>
                          ) : isCurrent && running ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 border border-brand/30 px-2.5 py-0.5 text-xs font-bold text-brand">
                              <span className="h-1.5 w-1.5 rounded-full bg-brand animate-ping" /> Up Now
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-soft dark:bg-stone-800 px-2.5 py-0.5 text-xs font-medium text-ink-3 dark:text-stone-300 border border-line dark:border-white/10">
                              Queued
                            </span>
                          )}
                        </td>
                        <td className="p-3.5 pr-4 text-right align-middle">
                          <div className="flex items-center justify-end gap-2">
                            {!done && !isCurrent && (
                              <button
                                onClick={() => {
                                  // Jump to this lead
                                  setAttemptedIds((prev) =>
                                    prev.includes(item.id) ? prev : prev.filter((id) => id !== item.id),
                                  );
                                  // Move other items before it into attempted
                                  const idx = items.findIndex((it) => it.id === item.id);
                                  const beforeIds = items.slice(0, idx).map((it) => it.id);
                                  setAttemptedIds((prev) => Array.from(new Set([...prev, ...beforeIds])));
                                  toast.success(`Jumped to ${item.name}`);
                                }}
                                className="opacity-0 group-hover:opacity-100 rounded-md bg-soft dark:bg-stone-800 px-2 py-1 text-xs font-semibold text-ink-2 dark:text-stone-200 hover:bg-line dark:hover:bg-stone-700 border border-line dark:border-white/15 transition-all"
                              >
                                Dial Now
                              </button>
                            )}
                            <Link
                              to={`/crm/leads/${item.id}`}
                              target="_blank"
                              className="rounded-lg p-1.5 text-ink-3 dark:text-stone-400 hover:bg-soft dark:hover:bg-stone-800 hover:text-ink dark:hover:text-white transition-colors"
                              title="Open full record"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredQueueItems.length === 0 && (
                <div className="p-8 text-center text-xs text-ink-3 dark:text-stone-400">
                  No leads found matching your filter criteria.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Follow-up / Appointment Scheduling Modal */}
      {followUpModalOpen && current && (
        <Modal
          title="Schedule Callback / Follow-Up"
          subtitle={`Set up next conversation with ${current.name} (${current.phone})`}
          onClose={() => setFollowUpModalOpen(false)}
          width="max-w-md"
          footer={
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setFollowUpModalOpen(false)}
                className="rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-4 py-2 text-sm font-semibold text-ink dark:text-stone-100 hover:bg-soft dark:hover:bg-stone-700"
              >
                Cancel
              </button>
              <button
                onClick={submitFollowUp}
                className="rounded-xl bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-hover shadow-sm"
              >
                Save & Advance
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-ink-2 dark:text-stone-300 uppercase tracking-wider mb-1.5">
                Callback Date & Time *
              </label>
              <DateTimePicker
                value={followUpDueAt}
                onChange={setFollowUpDueAt}
                placeholder="Pick callback date & time"
                className="w-full"
              />
            </div>

            {/* Quick date presets */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { label: "Tomorrow 10 AM", hours: 24, hourVal: 10 },
                { label: "Tomorrow 3 PM", hours: 24, hourVal: 15 },
                { label: "In 2 Days", hours: 48, hourVal: 11 },
                { label: "Next Week", hours: 168, hourVal: 10 },
              ].map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    d.setDate(d.getDate() + Math.floor(preset.hours / 24));
                    d.setHours(preset.hourVal, 0, 0, 0);
                    // format to YYYY-MM-DDTHH:mm
                    const tzoffset = d.getTimezoneOffset() * 60000;
                    const localISOTime = new Date(d.getTime() - tzoffset).toISOString().slice(0, 16);
                    setFollowUpDueAt(localISOTime);
                  }}
                  className="rounded-lg bg-soft dark:bg-stone-800 border border-line dark:border-white/15 px-2.5 py-1 text-xs font-medium text-ink-2 dark:text-stone-200 hover:bg-line dark:hover:bg-stone-700"
                >
                  {preset.label}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-xs font-bold text-ink-2 dark:text-stone-300 uppercase tracking-wider mb-1.5">
                What to Discuss / Notes
              </label>
              <input
                value={followUpNote}
                onChange={(e) => setFollowUpNote(e.target.value)}
                placeholder="e.g., Send quote, check budget approval"
                className="w-full rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 p-2.5 text-sm text-ink dark:text-stone-100 outline-none focus:border-brand placeholder:text-ink-faint dark:placeholder:text-stone-400"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Suppression / DND Confirmation Modal */}
      {dndModalOpen && current && (
        <Modal
          title="Add to DND / Suppression List"
          subtitle={`Stop all future calls and campaigns to ${current.phone}`}
          onClose={() => setDndModalOpen(false)}
          width="max-w-md"
          footer={
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDndModalOpen(false)}
                className="rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 px-4 py-2 text-sm font-semibold text-ink dark:text-stone-100 hover:bg-soft dark:hover:bg-stone-700"
              >
                Cancel
              </button>
              <button
                onClick={submitSuppression}
                className="rounded-xl bg-danger px-5 py-2 text-sm font-semibold text-white hover:bg-danger/90 shadow-sm"
              >
                Suppress & Skip
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="rounded-xl bg-danger-soft dark:bg-red-950/40 border border-danger/30 dark:border-red-800/60 p-3 text-xs text-danger dark:text-red-300 flex items-start gap-2.5">
              <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
              <p>
                This contact will be permanently blocked from auto-dialer queues and marketing batches across all campaigns.
              </p>
            </div>

            <div>
              <label className="block text-xs font-bold text-ink-2 dark:text-stone-300 uppercase tracking-wider mb-1.5">
                Reason for Suppression
              </label>
              <select
                value={dndReason}
                onChange={(e) => setDndReason(e.target.value)}
                className="w-full rounded-xl border border-line dark:border-white/20 bg-elevated dark:bg-stone-800 p-2.5 text-sm text-ink dark:text-stone-100 outline-none focus:border-brand"
              >
                <option value="Customer opt-out">Customer opt-out / requested do not call</option>
                <option value="DND registry">Registered on national DND</option>
                <option value="Wrong number">Wrong number / invalid contact</option>
                <option value="Hostile / abusive">Hostile / abusive</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </Modal>
      )}

      {/* Keyboard Shortcuts Reference Modal */}
      {showShortcuts && (
        <Modal
          title="Dialer Keyboard Shortcuts"
          subtitle="Speed through queues without touching your mouse"
          onClose={() => setShowShortcuts(false)}
          width="max-w-md"
          footer={
            <button
              onClick={() => setShowShortcuts(false)}
              className="rounded-xl bg-brand px-5 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
            >
              Got it
            </button>
          }
        >
          <div className="space-y-2 text-xs">
            {[
              { key: "Space", desc: "Start / Pause queue session" },
              { key: "S", desc: "Skip lead to next" },
              { key: "1", desc: "1-Click: Interested (Connected)" },
              { key: "2", desc: "1-Click: Schedule Callback" },
              { key: "3", desc: "1-Click: No Answer / Busy" },
              { key: "4", desc: "1-Click: Left Voicemail" },
              { key: "5", desc: "1-Click: Not Interested" },
              { key: "?", desc: "Toggle this shortcut guide" },
            ].map((shortcut) => (
              <div
                key={shortcut.key}
                className="flex items-center justify-between rounded-lg bg-soft dark:bg-stone-800 p-2.5 border border-line dark:border-white/10"
              >
                <span className="text-ink-2 dark:text-stone-200">{shortcut.desc}</span>
                <kbd className="rounded border border-line dark:border-white/20 bg-elevated dark:bg-stone-700 px-2 py-0.5 font-mono text-[11px] font-bold text-ink dark:text-stone-100 shadow-xs">
                  {shortcut.key}
                </kbd>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
